import React, { useState } from 'react';

/**
 * Generates the monkey patch template code as a string
 */
const generateMonkeyPatchTemplate = () => {
    return `// GPS Monkey Patch - Copy and paste into browser console
// This overrides navigator.geolocation to accept simulated GPS data

// IMPORTANT: Make sure to BLOCK real GPS permission in browser for this site
// or the browser might use real GPS instead of this monkey patch

window.fakeLocation = {
    coords: {
        latitude: 61.4978,  // Tampere starting coordinates
        longitude: 23.7610,
        accuracy: 10,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        speed: null
    },
    timestamp: Date.now()
};

const watchers = [];
let activeSimulationTimeout = null; // Track active simulation

// Store original functions to detect if monkey patch is active
const originalGetCurrentPosition = navigator.geolocation.getCurrentPosition;
const originalWatchPosition = navigator.geolocation.watchPosition;

// Override getCurrentPosition
navigator.geolocation.getCurrentPosition = function(success, error, options) {
    console.log('📍 Monkey-patched getCurrentPosition called');
    console.log('   Position:', window.fakeLocation.coords.latitude, window.fakeLocation.coords.longitude);
    success(window.fakeLocation);
};

// Override watchPosition
navigator.geolocation.watchPosition = function(success, error, options) {
    console.log('👀 Monkey-patched watchPosition called');
    console.log('   Position:', window.fakeLocation.coords.latitude, window.fakeLocation.coords.longitude);
    watchers.push(success);
    success(window.fakeLocation);
    return watchers.length;
};

// Override clearWatch
const originalClearWatch = navigator.geolocation.clearWatch;
navigator.geolocation.clearWatch = function(id) {
    console.log('🛑 clearWatch called for id:', id);
    // Keep watchers array intact for re-use
};

// Function to manually update position
window.updateGPS = function(lat, lng, heading, speed) {
    window.fakeLocation.coords.latitude = lat;
    window.fakeLocation.coords.longitude = lng;
    if (heading !== undefined) window.fakeLocation.coords.heading = heading;
    if (speed !== undefined) window.fakeLocation.coords.speed = speed;
    window.fakeLocation.timestamp = Date.now();
    watchers.forEach(callback => callback(window.fakeLocation));
    console.log('📍 GPS updated: ' + lat.toFixed(5) + ', ' + lng.toFixed(5));
};

// Function to simulate going off-route (teleport)
window.goOffRoute = function(distanceMeters = 100) {
    if (!window.fakeLocation || !window.fakeLocation.coords) {
        console.error("No active fake location found.");
        return;
    }
    // Rough approximation: 1 degree latitude ~ 111km
    const offset = distanceMeters / 111320; 
    
    const currentLat = window.fakeLocation.coords.latitude;
    const currentLng = window.fakeLocation.coords.longitude;
    
    // Jump North-East
    const newLat = currentLat + offset;
    const newLng = currentLng + offset;
    
    window.updateGPS(newLat, newLng);
    console.log('🔀 Teleported off-route by ' + distanceMeters + 'm to ' + newLat.toFixed(5) + ', ' + newLng.toFixed(5));
};

// Function to stop active simulation
window.stopDriving = function() {
    if (activeSimulationTimeout !== null) {
        clearTimeout(activeSimulationTimeout);
        activeSimulationTimeout = null;
        console.log('⏹️  Simulation stopped');
        return true;
    } else {
        console.log('⚠️  No active simulation to stop');
        return false;
    }
};

// Function to start driving simulation with step-based coordinates
window.startDriving = function(steps) {
    // Stop any existing simulation before starting a new one
    if (activeSimulationTimeout !== null) {
        console.log('⏸️  Stopping previous simulation...');
        window.stopDriving();
    }
    
    let index = 0;
    console.log('🚗 Starting GPS simulation with', steps.length, 'steps');
    
    const drive = () => {
        if (index >= steps.length) {
            console.log('🏁 Simulation complete!');
            activeSimulationTimeout = null;
            return;
        }
        
        const step = steps[index];
        window.updateGPS(step.lat, step.lng, step.heading, step.speed);
        index++;
        
        if (index < steps.length) {
            activeSimulationTimeout = setTimeout(drive, step.delay || 1000);
        } else {
            activeSimulationTimeout = null;
        }
    };
    
    drive();
};

console.log('✅ GPS Monkey Patch installed!');
console.log('   Use window.startDriving(steps) to start simulation');
console.log('   Use window.stopDriving() to stop simulation');
console.log('   Use window.goOffRoute(meters) to simulate wrong turn');
console.log('   Use window.updateGPS(lat, lng, heading, speed) for manual updates');
console.log('   Check window.fakeLocation to see current faked position');
console.log('');
console.warn('⚠️  If GPS jumps to wrong location: Block real GPS permission in browser!');`
};

/**
 * Component that provides copy-pastable monkey patch template
 */
const MonkeyPatchGenerator = () => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        const template = generateMonkeyPatchTemplate();
        navigator.clipboard.writeText(template).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div style={{ marginTop: '10px', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
            <div style={{ marginBottom: '5px', fontSize: '12px', fontWeight: 'bold' }}>
                GPS Simulation Setup
            </div>
            <div style={{ fontSize: '10px', marginBottom: '5px', color: '#666' }}>
                Step 1: Copy monkey patch to console
            </div>
            <button
                onClick={handleCopy}
                style={{
                    width: '100%',
                    padding: '8px',
                    cursor: 'pointer',
                    backgroundColor: copied ? '#4CAF50' : '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px'
                }}
            >
                {copied ? '✓ Copied!' : '📋 Copy Monkey Patch'}
            </button>
            <div style={{ fontSize: '9px', marginTop: '3px', color: '#999' }}>
                Paste into browser console (F12) once per session
            </div>
        </div>
    );
};

export default MonkeyPatchGenerator;
